import * as THREE from 'three/webgpu';

import { RENDER_ORDER, lightPoolMaterial } from './materials.js';

/**
 * @file `LightPoolBatch` — the pool of light a moving lamp throws on the road.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS AND WHY IT IS NOT AN EMITTER
 * ─────────────────────────────────────────────────────────────────────────────
 * A STREETLAMP's glow is `bake/Emitters.js`: a record pushed at level build,
 * summed into the night vertex-colour channel (`color1`) of every road vertex
 * it reaches, and then frozen. It costs nothing at runtime because it is not a
 * light — it is paint. That is also why a car cannot use it: the bake happens
 * once, per vertex, at build time, and a headlight moves.
 *
 * So a moving lamp gets the same deal `ShadowBlobs.js` gives a moving actor: an
 * instanced quad lying on the ground, one draw call for every beam in the city.
 * The two classes are deliberately shaped alike, with ONE difference that
 * matters — this pool is a per-frame REBUILD (`reset` → `add`* → `commit`)
 * rather than an acquire/release slot pool. A car's beams are a pure function
 * of its transform and its lamp state, both of which `CarFleet` already holds,
 * so re-deriving them every frame is cheaper than keeping a free list correct
 * across `removeCar`, a carjack handover and a traffic recycle. There is no
 * slot to leak.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **THE BEAM IGNORES DEPTH, and that is the whole reason it is one quad.**
 *   A depth-tested ground quad gets eaten by the world it lies on: a car on the
 *   verge throws a beam that sinks under the road slab beside it, and it is
 *   clipped at the kerb, at every rise in the grass, everywhere.
 *   Draping the beam over sampled ground heights was implemented and playtested
 *   — 8 strips per beam, each sloping between two `heightAt` samples, the same
 *   trick `DecalBuilder` uses for the country lane dashes. It STILL broke in
 *   many places (a drape only conforms where it samples, and a kerb is a
 *   vertical step no piecewise ribbon fits) while costing ~7x the instances, so
 *   the owner called it: `depthTest: false`, one quad, always drawn whole.
 *   Do not re-add the drape without re-reading that history — it is a solved
 *   negative result, not an unexplored idea.
 *   The known price is in `materials.js#lightPoolMaterial`: a beam shines
 *   THROUGH a wall standing between the camera and the road it lands on. It is
 *   tolerable because the layer is additive and dim, so it reads as a faint
 *   wash rather than a hole.
 *
 * • **It is a coplanar transparent ground layer**, so it takes an explicit
 *   fractional render order (`RENDER_ORDER.lightPool`, 1.8) or it strobes
 *   against the road paint at 1.5/1.6. See the table in `materials.js`.
 *
 * • **Additive colours are capped well under the bloom threshold.** Night bloom
 *   kicks in at 0.45 (`render/post.js`), the two head cones OVERLAP down the
 *   middle of the lane, and additive means the overlap is the SUM — so the
 *   number to check against 0.45 is `2 * BEAM.headColor * BEAM.opacity`, never
 *   a colour on its own. Two owner tuning passes have come out of that: the
 *   tint went 0.22 -> 0.154, then a global `opacity` of 0.3 landed on top, so
 *   the road peaks around 0.09 where the cones cross. There is a lot of
 *   headroom now; spend it on `opacity`, which is one number rather than three.
 *
 * • **Y-lift is +0.02 m, never `polygonOffset`** — WebGPU's polygon-offset
 *   semantics differ from WebGL's, project-wide rule.
 *
 * @module engine/render/LightPools
 */

/** @typedef {import('../types.js').Registry} Registry */

/**
 * Beam geometry and colour. Metres and LINEAR RGB.
 *
 * The cones are wide and short rather than narrow and long on purpose: at the
 * game's 52° pitch a long thin beam is foreshortened into a sliver, and what
 * reads as "this car has its lights on" is the WIDTH of the lit tarmac beside
 * the bonnet, not how far down the street it reaches.
 */
export const BEAM = Object.freeze({
  /** Lamp intensity below which a beam is not worth a quad. */
  minLamp: 0.04,
  /**
   * Global beam strength (owner-tuned). Multiplies the falloff mask, so the
   * real additive peak on the road is `headColor * opacity`, and where the two
   * cones cross it is twice that — that product is the number to check against
   * the night bloom threshold, never `headColor` alone.
   */
  opacity: 0.3,
  /** Metres above the car's ground Y. Vestigial now that the beam ignores
   *  depth, but it keeps the quad off the exact ground plane for free. */
  lift: 0.02,

  /** Forward cone, one per headlight cluster. */
  headLength: 9,
  headWidth: 4.2,
  /** Metres inboard of the body side the cluster sits — matches `carBuild`. */
  headInset: 0.3,
  /** Metres ahead of the nose the quad's near edge starts. */
  headAhead: 0.15,
  /** Tint before `opacity`. Two cones overlap, so the road's real peak is
   *  `2 * headColor * opacity`. */
  headColor: /** @type {[number,number,number]} */ ([0.154, 0.146, 0.116]),
});

// ⚠ THERE IS NO REAR WASH, and it is not an oversight. A red pool behind the
// car shipped alongside the cones, went through a round of tuning (gated at
// 0.75 so running tails could not trigger it, only real braking), and the owner
// cut it anyway: a brake lamp is a small bright source at bumper height, and a
// metre-scale glow on the tarmac behind every stopping car read as a paint
// spill rather than as light. HEADLIGHTS ONLY. `addBeam`'s `dir` still supports
// a reversed beam — that is the primitive staying honest, not a dormant plan.

export class LightPoolBatch {
  /**
   * @param {Object} [o]
   * @param {number} [o.max=256] Instance cap. Three beams per lit car, so this
   *   covers ~85 cars with their lamps on; the city's whole fleet is ~62.
   *   Overflow is DROPPED and counted (see {@link stats}), never grown mid-frame.
   * @param {number} [o.size=64] Falloff texture resolution.
   * @param {number} [o.nearHalf=0.16] Half-width at the lamp, as a fraction of
   *   the far half-width. The cone's throat — 0 is a point source, 1 a slab.
   * @param {number} [o.core=0.55] Fraction of the cone half-width that stays at
   *   full brightness before the edge falloff starts.
   * @param {Registry|null} [o.registry] Tracks the texture + material so
   *   `Level.dispose()` frees them.
   * @param {string} [o.name='lightPools']
   */
  constructor(o = {}) {
    const {
      max = 256,
      size = 64,
      nearHalf = 0.16,
      core = 0.55,
      registry = null,
      name = 'lightPools',
    } = o;

    if (!(max > 0)) throw new Error('LightPoolBatch: `max` must be > 0');

    this.max = max | 0;
    /** @type {number} Beams written since the last `reset`. */
    this.count = 0;
    this._dropped = 0;
    this._disposed = false;

    /** @type {THREE.Texture} */
    this.texture = registry
      ? registry.texture(`${name}:beam`, () => makeBeamTexture(size, nearHalf, core))
      : makeBeamTexture(size, nearHalf, core);

    const geo = beamQuad();
    this._alphaAttr = new THREE.InstancedBufferAttribute(new Float32Array(this.max), 1);
    this._tintAttr = new THREE.InstancedBufferAttribute(new Float32Array(this.max * 3), 3);
    this._alphaAttr.setUsage(THREE.DynamicDrawUsage);
    this._tintAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iAlpha', this._alphaAttr);
    geo.setAttribute('iTint', this._tintAttr);
    // The beams move every frame; a correct bounding sphere would have to be
    // recomputed every frame to save one draw call. Same trade as ShadowBlobs.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

    const mat = lightPoolMaterial({ tex: this.texture, opacity: BEAM.opacity, name });
    if (registry) registry.track?.(mat);

    /** @type {THREE.Material} */
    this.material = mat;

    /** @type {THREE.InstancedMesh} */
    this.object = new THREE.InstancedMesh(geo, mat, this.max);
    this.object.name = name;
    this.object.count = 0;
    this.object.renderOrder = RENDER_ORDER.lightPool;
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();
    this.object.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // `InstancedMesh` fills its matrices with IDENTITY (a unit quad at the
    // origin), not zero — an untouched slot has to be explicitly degenerate.
    this.object.instanceMatrix.array.fill(0);
    this.object.visible = false;
  }

  /** Drops last frame's beams. Call once per frame before the `add`s. */
  reset() {
    this.count = 0;
    this._dropped = 0;
  }

  /**
   * Writes one beam: ONE quad, lying flat at the car's ground height, drawn
   * without a depth test so nothing in the world can clip it. See the module
   * header for why it is not draped — that was tried and rejected.
   *
   * Placement is given in the CAR's frame so a caller never has to know the yaw
   * convention: `Ry(yaw)` maps local +X (the nose) to world `(cos yaw, −sin
   * yaw)`, which is the one sign the whole traffic system rests on.
   *
   * @param {number} cx @param {number} cy Ground height under the car — `lift`
   *   is added for you. @param {number} cz
   * @param {number} yaw Car yaw, radians.
   * @param {number} ox Beam origin, metres along the car's nose axis (+ forward).
   * @param {number} oz Beam origin, metres to the car's right.
   * @param {number} dir `+1` the beam shines at the nose, `−1` at the tail.
   * @param {number} length Metres from the origin to where the light dies.
   * @param {number} width Metres across at the far end.
   * @param {ArrayLike<number>} tint Linear RGB, length >= 3.
   * @param {number} alpha 0..1 — the lamp's own intensity.
   * @returns {boolean} `false` when the batch was full (and the beam dropped).
   */
  addBeam(cx, cy, cz, yaw, ox, oz, dir, length, width, tint, alpha) {
    if (this.count >= this.max) {
      this._dropped++;
      return false;
    }
    const id = this.count++;

    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    // Car-local (ox, oz) into world. Local +X -> (c, −s); local +Z -> (s, c).
    const wx = cx + ox * c + oz * s;
    const wz = cz - ox * s + oz * c;

    const e = this.object.instanceMatrix.array;
    const o = id * 16;
    const dl = dir * length;
    // Column-major. col0 = beam axis * length, col1 = up, col2 = across * width.
    e[o] = c * dl; e[o + 1] = 0; e[o + 2] = -s * dl; e[o + 3] = 0;
    e[o + 4] = 0; e[o + 5] = 1; e[o + 6] = 0; e[o + 7] = 0;
    e[o + 8] = s * width; e[o + 9] = 0; e[o + 10] = c * width; e[o + 11] = 0;
    e[o + 12] = wx; e[o + 13] = cy + BEAM.lift; e[o + 14] = wz; e[o + 15] = 1;

    this._alphaAttr.array[id] = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const t = id * 3;
    this._tintAttr.array[t] = tint[0];
    this._tintAttr.array[t + 1] = tint[1];
    this._tintAttr.array[t + 2] = tint[2];
    return true;
  }

  /** Uploads the frame's beams. Call once per frame after the `add`s. */
  commit() {
    if (this._disposed) return;
    this.object.instanceMatrix.needsUpdate = true;
    this._alphaAttr.needsUpdate = true;
    this._tintAttr.needsUpdate = true;
    this.object.count = this.count;
    this.object.visible = this.count > 0;
  }

  /** @returns {{count: number, max: number, dropped: number, draws: number}} */
  stats() {
    return { count: this.count, max: this.max, dropped: this._dropped, draws: this.count > 0 ? 1 : 0 };
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.object.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/**
 * A unit quad in the XZ plane with the UVs the beam texture wants: local X runs
 * 0 (at the lamp) to 1 (where the light dies) and maps to `u`; local Z runs
 * −0.5 to +0.5 across the beam and maps to `v`.
 *
 * Hand-built rather than a rotated `PlaneGeometry` because that one centres its
 * X span, and a beam has to PIVOT at the lamp — the instance matrix scales X by
 * the beam length, so a centred quad would put half the light inside the car.
 *
 * Non-indexed (project rule 3), two triangles.
 * @returns {THREE.BufferGeometry}
 */
export function beamQuad() {
  const g = new THREE.BufferGeometry();
  const pos = new Float32Array([
    0, 0, -0.5, 1, 0, -0.5, 1, 0, 0.5,
    0, 0, -0.5, 1, 0, 0.5, 0, 0, 0.5,
  ]);
  const uvs = new Float32Array([
    0, 0, 1, 0, 1, 1,
    0, 0, 1, 1, 0, 1,
  ]);
  const nor = new Float32Array([
    0, 1, 0, 0, 1, 0, 0, 1, 0,
    0, 1, 0, 0, 1, 0, 0, 1, 0,
  ]);
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  g.name = 'beamQuad';
  return g;
}

/**
 * The cone mask, in the ALPHA channel (all `lightPoolMaterial` reads).
 *
 * `u` is distance along the beam, `v` across it. The lit half-width GROWS with
 * `u` — that is what makes the quad read as a cone rather than a rectangle —
 * from `nearHalf` at the lamp to exactly 1.0 (the quad edge) at the far end, so
 * the mask reaches zero on the boundary and there is no hard seam to see.
 *
 * Along the beam: a fast ramp in over the first 10% (without it the beam starts
 * as a bright BAR across the bumper) and a long decay from 35% out, squared for
 * a light-like curve rather than a linear cone.
 *
 * Arithmetic, not a canvas `createRadialGradient` — same image, minus the DOM,
 * which is what lets this construct in node and in the test suite.
 *
 * @param {number} [size=64] Texture edge, pixels.
 * @param {number} [nearHalf=0.16] Cone half-width at the lamp, 0..1.
 * @param {number} [core=0.55] Fraction of the half-width held at full alpha.
 * @returns {THREE.DataTexture}
 */
export function makeBeamTexture(size = 64, nearHalf = 0.16, core = 0.55) {
  const n = Math.max(4, size | 0);
  const data = new Uint8Array(n * n * 4);
  const throat = Math.min(Math.max(nearHalf, 0.01), 0.9);
  const solid = Math.min(Math.max(core, 0), 0.95);

  for (let y = 0; y < n; y++) {
    // v: 0..1 across the beam. Row centre, so the mask is symmetric.
    const v = (y + 0.5) / n;
    const across = Math.abs(v - 0.5) * 2; // 0 centre .. 1 edge
    for (let x = 0; x < n; x++) {
      const u = (x + 0.5) / n; // 0 at the lamp .. 1 at the far end
      const half = throat + (1 - throat) * Math.sqrt(u);
      const lateral = 1 - smoothstep(half * solid, half, across);
      const along = smoothstep(0, 0.1, u) * (1 - smoothstep(0.35, 1, u));
      const a = lateral * along * along;

      const i = (y * n + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(Math.min(1, Math.max(0, a)) * 255);
    }
  }

  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  tex.name = 'beamFalloff';
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * @param {number} a @param {number} b @param {number} x
 * @returns {number} 0..1
 */
function smoothstep(a, b, x) {
  const t = b === a ? 0 : (x - a) / (b - a);
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}
